import type { AccountIdentifierFieldDef } from './types';

/** Maps integer id field names to their uuid counterparts for the USE_ACCOUNT_UUID contract. */
const ACCOUNT_IDENTIFIER_PAIRS = {
  id: 'uuid',
  accountId: 'accountUuid',
  accountVerifiedBy: 'accountVerifiedByUuid',
  createdBy: 'createdByUuid',
  updatedBy: 'updatedByUuid',
  slideAuthor: 'slideAuthorUuid',
  reportedBy: 'reportedByUuid',
  accessAccount: 'accessAccountUuid',
  userId: 'userUuid',
  apiUserId: 'apiUserUuid',
  contactId: 'contactUuid',
  triggeredBy: 'triggeredByUuid',
  ownerId: 'ownerUuid',
} as const;

type AccountIdField = keyof typeof ACCOUNT_IDENTIFIER_PAIRS;
type AccountUuidField = (typeof ACCOUNT_IDENTIFIER_PAIRS)[AccountIdField];

/** Audit trail fields shared across entity and ref field groups. */
const AUDIT_FIELDS: readonly AccountIdentifierFieldDef[] = [
  { id: 'createdBy', uuid: 'createdByUuid' },
  { id: 'updatedBy', uuid: 'updatedByUuid' },
];

/** Use when the response object IS an account. Includes the account's own PK plus audit fields. */
const ACCOUNT_ENTITY_FIELDS: readonly AccountIdentifierFieldDef[] = [
  { id: 'id', uuid: 'uuid' },
  { id: 'accountId', uuid: 'accountUuid' },
  { id: 'accountVerifiedBy', uuid: 'accountVerifiedByUuid' },
  ...AUDIT_FIELDS,
];

/** Use when the response object references an account but is not itself an account (e.g. admin records, brief access). Does not include bare id/uuid to avoid stripping the entity's own PK. */
const ACCOUNT_REF_FIELDS: readonly AccountIdentifierFieldDef[] = [
  { id: 'accountId', uuid: 'accountUuid' },
  { id: 'slideAuthor', uuid: 'slideAuthorUuid' },
  { id: 'reportedBy', uuid: 'reportedByUuid' },
  { id: 'accessAccount', uuid: 'accessAccountUuid' },
  { id: 'userId', uuid: 'userUuid' },
  { id: 'apiUserId', uuid: 'apiUserUuid' },
  { id: 'contactId', uuid: 'contactUuid' },
  { id: 'triggeredBy', uuid: 'triggeredByUuid' },
  ...AUDIT_FIELDS,
];

/** Use for access request responses where recipient and requestor are nested account objects. */
const ACCESS_REQUEST_NESTED_FIELDS = {
  recipient: ACCOUNT_ENTITY_FIELDS,
  requestor: ACCOUNT_ENTITY_FIELDS,
} as const;

/**
 * Use for bulk access granted/accessRequested/failed arrays. The principalId/principalUuid
 * pair only applies to user-type entries (groups and orgs don't have account uuids).
 */
const BULK_ACCESS_RESULT_FIELDS: readonly AccountIdentifierFieldDef[] = [
  { id: 'principalId', uuid: 'principalUuid', when: { field: 'principalType', equals: 'user' } },
];

/** Use for brief list/detail routes; the ownerId/ownerUuid pair only applies to account-type owners. */
const BRIEF_OWNER_FIELDS: readonly AccountIdentifierFieldDef[] = [
  { id: 'ownerId', uuid: 'ownerUuid', when: { field: 'ownerType', equals: 'account' } },
  ...ACCOUNT_REF_FIELDS,
];

/**
 * All field names that participate in the USE_ACCOUNT_UUID contract.
 * Both sides of every identifier pair are listed so we can make them
 * optional on response types via {@link WithContractedIdentity}.
 */
type ContractedIdentityField = AccountIdField | AccountUuidField;

export {
  ACCOUNT_IDENTIFIER_PAIRS,
  ACCOUNT_ENTITY_FIELDS,
  ACCOUNT_REF_FIELDS,
  ACCESS_REQUEST_NESTED_FIELDS,
  AUDIT_FIELDS,
  BULK_ACCESS_RESULT_FIELDS,
  BRIEF_OWNER_FIELDS,
};
export type { AccountIdField, AccountUuidField, ContractedIdentityField };
